import React, { useState } from 'react';
import clsx from 'clsx';

// On-screen numeric keypad ("number getter") shown on mobile when a numeric
// field is focused. Ported from dydx-exchange, simplified: plain `.` decimal,
// no sound, no i18n. Resolves the entered value through `onClose`.
const NumericKeypad = ({ initialValue = '', onClose, prepend, min, max, allowDecimals = true }) => {
	const [value, setValue] = useState(initialValue == null ? '' : initialValue.toString());

	const atMax = typeof max === 'number' && value !== '' && parseFloat(value) >= max;

	const extendValue = char => {
		if (char === '.' && (!allowDecimals || value.includes('.'))) {
			return;
		}
		let newValue = value + char;
		if (char !== '.' && typeof max === 'number' && parseFloat(newValue) > max) {
			newValue = max.toString();
		}
		setValue(newValue);
	};

	const reduceValue = () => {
		if (value.length > 0) {
			setValue(value.slice(0, -1));
		}
	};

	const handleDone = () => {
		let finalValue = value;
		if (finalValue === '' || finalValue === '-' || isNaN(parseFloat(finalValue))) {
			finalValue = typeof min === 'number' ? min : 0;
		} else {
			finalValue = parseFloat(finalValue);
			if (typeof min === 'number' && finalValue < min) {
				finalValue = min;
			}
			if (typeof max === 'number' && finalValue > max) {
				finalValue = max;
			}
		}
		onClose(finalValue);
	};

	const handleCancel = () => {
		const original = initialValue === '' || initialValue == null ? undefined : parseFloat(initialValue);
		onClose(original);
	};

	const digit = char => (
		<button
			disabled={atMax}
			className="btn-keypad"
			onClick={() => extendValue(char)}
			type="button"
			key={char}
		>
			{char}
		</button>
	);

	const displayValue = value === '' ? '0' : value;

	let prependNode;
	if (prepend) {
		prependNode = <span className="text-base">{prepend}</span>;
	}

	let maxNode;
	if (typeof max === 'number') {
		maxNode = <span className="absolute text-sm text-gray-600 bottom-1 right-2">max. {max}</span>;
	}

	return (
		<div className="md:hidden fixed bottom-0 left-0 w-screen native-mobile-padding">
			<div className="w-full bg-gray-400 p-4 pt-6 rounded-t-lg">
				<div className="unselectable relative overflow-x-hidden px-2 w-full rounded-lg bg-gray-200 text-2xl h-12 flex flex-col items-center justify-center text-black shadow-[inset_0_0_2px_rgba(0,0,0,0.9)]">
					<span className="flex flex-row gap-1 items-baseline">
						{displayValue}
						{prependNode}
					</span>
					{maxNode}
				</div>

				<div className="grid grid-cols-3 gap-2 my-4">
					{['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(digit)}
					<button
						disabled={!allowDecimals || value.includes('.') || atMax}
						className="btn-keypad"
						onClick={() => extendValue('.')}
						type="button"
					>
						.
					</button>
					{digit('0')}
					<button className="btn-keypad" onClick={reduceValue} type="button" aria-label="backspace">
						⌫
					</button>
				</div>

				<div className="flex flex-row w-full gap-2 justify-between">
					<button className={clsx('flex-1 btn btn-bordered capitalize text-base')} onClick={handleCancel} type="button">
						cancel
					</button>
					<button className="flex-1 btn btn-bordered capitalize text-base" onClick={handleDone} type="button">
						done
					</button>
				</div>
			</div>
		</div>
	);
};

export default NumericKeypad;
